import { useRouter } from 'next/router';
import { useLang } from '@/i18n';
import EcoLink from 'eco-design-components/base/eco-link';
import { useState } from 'react';
import css from './index.module.scss';
import EFVideoModal from '@/components/UI/EFVideoModal';
import { useProductPageStatus } from '../../ProductProvider';
import { IPageStatus } from '../../config/IPageStatus';
import { fetchPost } from 'lowcode/utils/service/FetchClient';
import { baseUrl } from 'lowcode/utils/service/baseUrl';
import { links } from '../B/links';
import { checks } from '../B/checks';
import gtm from '@/utils/gtm';
import { useScheduledContent } from '@/utils/hooks/useScheduledContent';
import EfVideo from '@elem/ui/ef-video';
import { subscribeCookie } from '@/utils/cookies/subscribeCookie';

export const getEmailConfigs = (dateString: string) => ({
    us: {
        contactListId: ['986635575'],
        fields: [
            {
                id: '4423',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '31',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '11150',
                type: 'multiple',
                value: ['14'],
                overwrite: false,
            },
            {
                id: '12637',
                type: 'text',
                value: dateString,
                overwrite: false,
            },
        ],
    },
    ca: {
        contactListId: ['2097466008'],
        fields: [
            {
                id: '4424',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '31',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '11150',
                type: 'multiple',
                value: ['14'],
                overwrite: false,
            },
            {
                id: '12637',
                type: 'text',
                value: dateString,
                overwrite: false,
            },
        ],
    },
    jp: {
        contactListId: ['503090051'],
        fields: [
            {
                id: '4431',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '31',
                type: 'single',
                value: 1,
                overwrite: false,
            },
            {
                id: '12713',
                type: 'multiple',
                value: ['1'],
                overwrite: false,
            },
            {
                id: '12712',
                type: 'text',
                value: dateString,
                overwrite: false,
            },
        ],
    },
    eu: {
        contactListId: ['1585617461'],
        fields: [
            {
                id: '4427',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '31',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '7596',
                type: 'multiple',
                value: ['49'],
                overwrite: false,
            },
            {
                id: '9690',
                type: 'multiple',
                value: ['12'],
                overwrite: false,
            },
        ],
    },
    uk: {
        contactListId: ['1372111406'],
        fields: [
            {
                id: '4430',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '31',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '7596',
                type: 'multiple',
                value: ['49'],
                overwrite: false,
            },
            {
                id: '9690',
                type: 'multiple',
                value: ['12'],
                overwrite: false,
            },
        ],
    },
    de: {
        contactListId: ['356112763'],
        fields: [
            {
                id: '4425',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '31',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '7596',
                type: 'multiple',
                value: ['49'],
                overwrite: false,
            },
            {
                id: '9690',
                type: 'multiple',
                value: ['12'],
                overwrite: false,
            },
        ],
    },
    fr: {
        contactListId: ['1615613479'],
        fields: [
            {
                id: '4428',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '31',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '7596',
                type: 'multiple',
                value: ['49'],
                overwrite: false,
            },
            {
                id: '9690',
                type: 'multiple',
                value: ['12'],
                overwrite: false,
            },
        ],
    },
    it: {
        contactListId: ['1004671813'],
        fields: [
            {
                id: '4429',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '31',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '7596',
                type: 'multiple',
                value: ['49'],
                overwrite: false,
            },
            {
                id: '9690',
                type: 'multiple',
                value: ['12'],
                overwrite: false,
            },
        ],
    },
    es: {
        contactListId: ['1212554384'],
        fields: [
            {
                id: '4426',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '31',
                type: 'single',
                value: 1,
                overwrite: true,
            },
            {
                id: '7596',
                type: 'multiple',
                value: ['49'],
                overwrite: false,
            },
            {
                id: '9690',
                type: 'multiple',
                value: ['12'],
                overwrite: false,
            },
        ],
    },
});

const A: React.FC = () => {
    const { locale = '' } = useRouter();
    const { t } = useLang('rapidpro');
    const [visible, setVisible] = useState(false);
    const pageStatus = useProductPageStatus();
    const [input, setInput] = useState<string>('');
    const [tip, setTip] = useState<string>('');
    const [link1, link2] = links[locale] || links.us;
    const [check, setCheck] = useState<boolean>(checks[locale] || checks.us);
    const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const handleFocus = () => {
        if (tip) setTip('');
    };

    const handleBlur = () => {
        if (!input) {
            return setTip(t('B_subscribe_empty'));
        }
        const email = input.trim();
        if (!regex.test(email)) {
            return setTip(t('B_subscribe_error'));
        }
        if (!check) {
            return setTip(t('B_subscribe_check'));
        }
    };

    const handleCheck = (e: HTMLInputElement) => {
        setCheck(e.checked);
    };

    const handleSubmit = async () => {
        const email = input.trim();
        if (!email) {
            return setTip(t('B_subscribe_empty'));
        }
        if (!regex.test(email)) {
            return setTip(t('B_subscribe_error'));
        }
        if (!check) {
            return setTip(t('B_subscribe_check'));
        }
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        const emailConfig = getEmailConfigs(dateString);
        const { contactListId, fields } = emailConfig[locale as keyof typeof emailConfig] || emailConfig['us'];
        const res = await fetchPost(
            `${baseUrl(locale)}/subscribe/new`,
            locale,
            {
                contactListId: contactListId[0],
                email,
                fields,
                source: 'WEB_NEW_PRODUCT_SUB',
                otherContactListIds: contactListId.slice(1),
                eventTracking: subscribeCookie({
                    sector_name: t('A_title'),
                    entry_position: '1',
                }),
            },
            {
                headers: {
                    // apiKey: '527760f6df93c0d6e98609fa8e8a1f4a',
                },
            },
        );
        if (res?.code === '0') {
            setTip(t('B_subscribe_success2'));
            setInput('');
            setTimeout(() => {
                setTip('');
            }, 3000);
            gtm.push({
                event: 'uaEvent',
                event_name: 'activity_submit',
                button_name: t('B_submit'),
                status: 'success',
                tag: 'Agree',
                product_name: 'EcoFlow Rapid Pro',
                __btn_id1: `${css.section} .submit`,
                __btn_id2: t('B_submit'),
                __btn_type: 'text',
            });
        } else {
            setTip(res?.message);
            console.error(res?.message);
            gtm.push({
                event: 'uaEvent',
                event_name: 'activity_submit',
                button_name: t('B_submit'),
                status: 'fail',
                tag: 'Agree',
                product_name: 'EcoFlow Rapid Pro',
                __btn_id1: `${css.section} .submit`,
                __btn_id2: t('B_submit'),
                __btn_type: 'text',
            });
        }
    };

    const handleOpenYoutube = () => {
        setVisible(true);
        gtm.push({
            event: 'uaEvent',
            event_name: 'video',
            title_name: t('A_title'),
            position: 'Product Detail_1',
            __btn_id1: `${css.section} .btn-video`,
            __btn_id2: t('A_btn_youtube') || 'Watch the Video',
            __btn_type: 'common',
        });
    };

    const IS_JP = locale === 'jp';
    const IS_NA = ['us', 'ca', 'ph'].includes(locale);
    const IS_EU = ['eu', 'uk', 'de', 'fr', 'es', 'it'].includes(locale);
    const buy = useScheduledContent({
        '2025/06/24 15:00:00 GMT+8': t('presale'),
        '2025/07/15 15:00:00 GMT+8': t('buy_now'),
    });

    return (
        <div id="rapid-pro-A" className={`rapid-pro-A ${css.section} GtmModuleData-section`} data-section-id={'rapid-pro-A'} data-section-title={t('A_title')}>
            <div className="banner-img">
                <EfVideo
                    autoPlay
                    pc="https://websiteoss.ecoflow.com/upload/video/1749202903700/1.mp4"
                    pad="https://websiteoss.ecoflow.com/upload/video/1749202903700/1.mp4"
                    h5="https://websiteoss.ecoflow.com/upload/video/1749202903543/1_M.mp4"
                    poster="https://websiteoss.ecoflow.com/upload/image/1749007320340/1.jpg"
                    posterPad="https://websiteoss.ecoflow.com/upload/image/1749007320340/1.jpg"
                    posterH5="https://websiteoss.ecoflow.com/upload/image/1749007320160/1_M.jpg"
                    preload="auto"
                    fetchpriority="high"
                />
            </div>
            <div className="banner-info">
                <p className="banner-info-label">{t('A_label')}</p>
                <p className="banner-info-title">{t('A_title')}</p>
                {t('A_desc') && <p className="banner-info-desc">{t('A_desc')}</p>}
                <div className="banner-info-btns">
                    <EcoLink
                        className={`banner-info-btns-btn banner-info-btns-btn-buy btn-buy GtmModuleData-btn-href-${IS_EU ? 'href' : 'common'}`}
                        data-btn-id={IS_EU ? buy : t('buy_now')}
                        href={
                            IS_EU
                                ? `https://${locale}.ecoflow.com/collections/power-bank?sort_by=best-selling&filter.p.m.custom.filter_series=RAPID+Pro`
                                : `#rapid-pro-C`
                        }
                        dataLayer={{
                            event: 'uaEvent',
                            event_name: 'buy',
                            product_name: 'EcoFlow Rapid Pro',
                            position: 'Collections_EcoFlow RAPID Pro',
                            __btn_id1: `${css.section} .btn-buy`,
                            __btn_id2: IS_EU
                                ? `https://${locale}.ecoflow.com/collections/power-bank?sort_by=best-selling&filter.p.m.custom.filter_series=RAPID+Pro`
                                : `#rapid-pro-C`,
                            __btn_type: IS_EU ? 'href' : 'common',
                        }}
                    >
                        {IS_EU ? buy : t('buy_now')}
                        {IS_EU && (
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                                <path
                                    d="M6.33301 4.08887L10.333 8.08887L6.33301 12.0889"
                                    stroke="#3F68E0"
                                    strokeWidth="1.33333"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        )}
                    </EcoLink>
                    {IS_EU && (
                        <EcoLink
                            className="banner-info-btns-btn banner-info-btns-btn-video GtmModuleData-btn-common btn-video"
                            data-btn-id={t('A_btn_youtube')}
                            onClick={handleOpenYoutube}
                            dataLayer={{
                                event: 'uaEvent',
                                event_name: 'video',
                                title_name: 'EcoFlow Rapid Pro',
                                position: 'Product Detail_1',
                                __btn_id1: `${css.section} .btn-video`,
                                __btn_id2: t('A_btn_youtube'),
                                __btn_type: 'common',
                            }}
                        >
                            {t('A_btn_youtube')}
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.5 0C4.08172 0 0.5 3.58172 0.5 8C0.5 12.4183 4.08172 16 8.5 16C12.9183 16 16.5 12.4183 16.5 8C16.5 3.58172 12.9183 0 8.5 0ZM7.53428 11.0065L11.2508 8.32435C11.472 8.1647 11.472 7.8353 11.2508 7.67565L7.53428 4.99351C7.26973 4.80259 6.9002 4.99162 6.9002 5.31786L6.9002 10.6821C6.9002 11.0084 7.26973 11.1974 7.53428 11.0065Z"
                                    fill="#3F68E0"
                                />
                            </svg>
                        </EcoLink>
                    )}
                    {!IS_EU && (
                        <button
                            className="banner-info-btns-btn btn-video GtmModuleData-btn-common banner-info-btns-btn-youtube"
                            data-btn-id={t('A_btn_youtube') || 'Watch the Video'}
                            onClick={handleOpenYoutube}
                        >
                            <svg className="border-svg svg-pc" xmlns="http://www.w3.org/2000/svg" width="195" height="49" viewBox="0 0 195 49" fill="none">
                                <path
                                    d="M184.293 1.08887L194 10.7959V38.3818L184.293 48.0889H10.667L1 40.8379V8.8291L10.6758 1.08887H184.293Z"
                                    stroke="currentColor"
                                />
                            </svg>
                            <svg className="border-svg svg-h5" xmlns="http://www.w3.org/2000/svg" width="170" height="48" viewBox="0 0 170 48" fill="none">
                                <path d="M161.011 0.5L169.5 10.1875V37.8115L161.011 47.5H8.94727L0.5 40.2695V8.21973L8.95605 0.5H161.011Z" stroke="#D9D9D9" />
                            </svg>
                            <div className="banner-info-btns-btn-inner">
                                <span className="banner-info-btns-btn-text">{t('A_btn_youtube')}</span>
                                <svg className="icon-svg" xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M10.5 0.588867C4.97715 0.588867 0.5 5.06602 0.5 10.5889C0.5 16.1117 4.97715 20.5889 10.5 20.5889C16.0228 20.5889 20.5 16.1117 20.5 10.5889C20.5 5.06602 16.0228 0.588867 10.5 0.588867ZM9.2926 14.347L13.9382 10.9943C14.2147 10.7947 14.2147 10.383 13.9382 10.1834L9.2926 6.83075C8.96192 6.5921 8.5 6.82839 8.5 7.23619V13.9415C8.5 14.3493 8.96192 14.5856 9.2926 14.347Z"
                                        fill="white"
                                    />
                                </svg>
                            </div>
                        </button>
                    )}
                </div>
                {pageStatus === IPageStatus.MEMBER && !IS_NA && (
                    <>
                        <div className="subscribe-control">
                            <input
                                type="text"
                                className="subscribe-control-input"
                                placeholder={t('B_placeholder')}
                                value={input}
                                onInput={handleInput}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                            />
                            <button className="subscribe-control-button submit GtmModuleData-btn-text" onClick={handleSubmit}>
                                {t('B_submit')}
                            </button>
                        </div>
                        <div className="check">
                            <input id="rapid-pro-check-A" type="checkbox" className="checkbox" checked={check} onChange={handleCheck} />
                            {link1 && link2 && (
                                <label className="check-text" htmlFor="rapid-pro-check-A">
                                    {t.rich('B_note', {
                                        link1: (tx: string) => (
                                            <EcoLink
                                                className="link-1 GtmModuleData-btn-href"
                                                href={link1}
                                                dataLayer={{
                                                    __btn_id1: `${css.section} .link-1`,
                                                    __btn_id2: link1,
                                                    __btn_type: 'href',
                                                }}
                                            >
                                                {tx}
                                            </EcoLink>
                                        ),
                                        link2: (tx: string) => (
                                            <EcoLink
                                                className="link-2 GtmModuleData-btn-href"
                                                href={link2}
                                                dataLayer={{
                                                    __btn_id1: `${css.section} .link-2`,
                                                    __btn_id2: link2,
                                                    __btn_type: 'href',
                                                }}
                                            >
                                                {tx}
                                            </EcoLink>
                                        ),
                                    })}
                                </label>
                            )}
                            {tip && <p className={`subscribe-tip${t('B_subscribe_success2') === tip ? ' success' : ''}`}>{tip}</p>}
                        </div>
                    </>
                )}
            </div>
            <EFVideoModal
                visible={visible}
                source={(() => {
                    const urls = {
                        jp: 'https://www.youtube.com/embed/U9TXP0UCKEs',
                        fr: 'https://www.youtube.com/embed/NPn65Y4v7B0',
                        it: 'https://www.youtube.com/embed/znkA1SZvXiE',
                        es: 'https://www.youtube.com/embed/oVSJrvsuHYM',
                        eu: 'https://www.youtube.com/embed/H2VGuNBzsGI',
                        uk: 'https://www.youtube.com/embed/H2VGuNBzsGI',
                        de: 'https://www.youtube.com/embed/bb-qeO6u_9A',
                        us: 'https://www.youtube.com/embed/H2VGuNBzsGI',
                        ca: 'https://www.youtube.com/embed/H2VGuNBzsGI',
                        ph: 'https://www.youtube.com/embed/H2VGuNBzsGI',
                    };
                    return urls[locale as 'jp'] || urls.us;
                })()}
                onClose={() => setVisible(false)}
                playerType={locale === 'cn' ? 'Alibaba' : 'YouTube'}
            />
        </div>
    );
};

export default A;
